<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import Header from "@/components/reusables/CommonHeader.vue";
import CreateCollectionStep1 from "@/components/stickers/CreateCollectionStep1.vue";
import CreateCollectionStep2 from "@/components/stickers/CreateCollectionStep2.vue";
import CreateCollectionStep3 from "@/components/stickers/CreateCollectionStep3.vue";
import CreateCollectionStep4 from "@/components/stickers/CreateCollectionStep4.vue";
import CreateCollectionStep5 from "@/components/stickers/CreateCollectionStep5.vue";
import StickerButtons from "@/components/stickers/StickerButtons.vue";
import { storeToRefs } from "pinia";

const heading = ref("Create New Collection");
const goBackHref = ref("/stickers");
const goBackText = ref("Back to sticker collection");
const description = ref(
  "Follow the five steps to create a new collection and upload stickers.",
);
import { useRouter } from "vue-router";
const router = useRouter();
const currentStep = ref(1);
const totalSteps = ref(5);
import { userStickers } from "@/stores/stickers";
const stickerStore = userStickers();
const { createCollectionReqestParams, notificationObj } =
  storeToRefs(stickerStore);

const showSpinner = ref(false);
const step3Ref = ref<InstanceType<typeof CreateCollectionStep3>>();
const step4Ref = ref<InstanceType<typeof CreateCollectionStep4>>();
const step5Ref = ref<InstanceType<typeof CreateCollectionStep5>>();

onMounted(() => {
  try {
    showSpinner.value = true;
    stickerStore.reset();
  } catch (e: any) {
    console.log("Error in retrieving category details");
  } finally {
    showSpinner.value = false;
  }
});

const goToNextStep = async () => {
  stickerStore.populateNotificationObj({
    show: false,
    message: "",
    status: "",
  });
  switch (currentStep.value) {
    case 1:
      stickerStore.createCollectionProgress.step1 = true;
      if ((createCollectionReqestParams.value.stickers?.length ?? 0) > 0) {
        currentStep.value++;
      } else {
        stickerStore.populateNotificationObj({
          show: true,
          message: "Please upload atleast one svg sticker",
          status: "error",
        });
        setTimeout(() => {
          stickerStore.resetNotification();
        }, 4000);
      }
      break;
    case 2:
      stickerStore.createCollectionProgress.step2 = true;
      if (
        createCollectionReqestParams.value.svName !== "" &&
        createCollectionReqestParams.value.enName !== ""
      ) {
        currentStep.value++;
      }
      /* if (createCollectionReqestParams.value.categories?.length > 0) {
        currentStep.value++;
      } else {
        stickerStore.populateNotificationObj({
          show: true,
          message: "Please select atleast one category",
          status: "error",
        });
        setTimeout(() => {
          stickerStore.resetNotification();
        }, 4000);
      } */
      break;
    case 3:
      stickerStore.createCollectionProgress.step3 = true;
      if (step3Ref.value?.validate()) {
        currentStep.value++;
      }
      break;
    case 4:
      stickerStore.createCollectionProgress.step4 = true;
      if (step4Ref.value?.validate()) {
        currentStep.value++;
      }
      break;
    case 5:
      stickerStore.createCollectionProgress.step5 = true;
      /* Removing validation for step 5 as it is not required to have tags in the collection
       if (!step5Ref.value?.validate()) {
        break;
      } */
      try {
        showSpinner.value = true;
        await stickerStore.createStickerCollection();
        stickerStore.populateNotificationObj({
          show: false,
          heading: "Collection successfully added – not yet available to users",
          message:
            "Click “edit” on collection card in order to complete the upload process",
          status: "success",
        });
        stickerStore.reset();
        router.push("/stickers");
      } catch (e: any) {
        stickerStore.populateNotificationObj({
          show: true,
          message: "There was an error in creating collection",
          status: "error",
        });
        setTimeout(() => {
          stickerStore.resetNotification();
        }, 4000);
      } finally {
        showSpinner.value = false;
      }
      break;
    default:
      break;
  }
};

const secondaryButtonClicked = () => {
  stickerStore.populateNotificationObj({
    show: false,
    message: "",
    status: "",
  });
  if (currentStep.value === 1) {
    router.push("/stickers");
  } else {
    currentStep.value--;
  }
};

const primaryButtonText = computed(() => {
  if (currentStep.value === 5) {
    return "Save Collection";
  }
  return "Next";
});

const secondaryButtonText = computed(() => {
  if (currentStep.value === 1) {
    return "Cancel";
  }
  return "Previous";
});
</script>

<template>
  <pn-toast
    class="bottom-toast"
    :appearance="notificationObj.status"
    v-show="notificationObj.show"
    :heading="notificationObj.heading"
    :text="notificationObj.message"
    temporary
  ></pn-toast>
  <Header
    :heading="heading"
    :description="description"
    :go-back-href="goBackHref"
    :go-back-text="goBackText"
    :show-stepper="true"
    :total-steps="totalSteps"
    :current-step="currentStep"
  ></Header>
  <div :class="[{ spinner: showSpinner }]">
    <pn-spinner size="3" v-show="showSpinner" />
  </div>

  <CreateCollectionStep1 v-if="currentStep == 1" />
  <CreateCollectionStep2 v-if="currentStep == 2" />
  <CreateCollectionStep3 v-if="currentStep == 3" ref="step3Ref" />
  <CreateCollectionStep4 v-if="currentStep == 4" ref="step4Ref" />
  <CreateCollectionStep5 v-if="currentStep == 5" ref="step5Ref" />

  <StickerButtons
    :primary-button-text="primaryButtonText"
    :secondary-button-text="secondaryButtonText"
    @click-primary="goToNextStep"
    @click-secondary="secondaryButtonClicked"
  ></StickerButtons>
</template>

<style lang="scss" scoped>
.name-desc {
  display: flex;
  flex-direction: column;
  padding-left: 32px;
}

.width-67 {
  width: 67%;
}

.gap-32 {
  gap: 32px;
}

.buttons {
  display: flex;
  padding-top: 24px;
  margin-left: 32px;
  gap: 8px;
  justify-content: flex-start;
}

.bottom-toast {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  max-width: calc(100vw - 32px);
  text-align: center;
  z-index: 1000;
}
</style>
